import * as React from 'react';

import { usePostMessageHandler } from './PostMessageProvider';

import { CartType, PdpType } from 'src/types/Events';
import { ICustomText, IPhrases } from 'src/types/Strings';
import { IContract } from 'src/types/IContract';

// TODO: this is incomplete
export interface IConfig {
  // Theme
  displayName: string;
  logo: string | null;
  color: string;

  // Product Page CTA info
  type: PdpType;
  contracts?: IContract[];

  // Cart CTA info
  cartType: CartType;

  // Features
  invertCTAColors: boolean;
  prioritizeTermsPosition: boolean;
  showCtaPdpDeclineOption: boolean;
  showMonthlyPrice: boolean;
  showMostPopularBadge: boolean;
  socialProofNotification: 'off' | 'contractsSoldCount' | 'testimonial' | 'countAndTestimonial',
  affirm?: {
    showAffirm: boolean;
    maximumAmount: number;
    minimumAmount: number;
    pubKey: string;
    apiUrl: string;
  };

  // Language
  language: string;
  phrases: IPhrases;

  // Optional text overrides
  customText: ICustomText;

  fullWidth?: boolean; // Widget carts should expand to fill the container

  experiments: string[];

  isGlobalCart: boolean;

  defaultPlanSelectedOption: number;
  showXCoverBranding: boolean | null;
  showAgeRestrictionDisclaimer?: boolean;
  customStyleSheet: string;
  showIpidDisclaimer: boolean;
}

interface IInitDetails {
  globalCart?: boolean;
}

interface ConfigContext {
  config: IConfig;
  initDetails: IInitDetails;
}

const ConfigContext = React.createContext<ConfigContext | null>(null);

export const ConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const [config, setConfig] = React.useState<IConfig | null>(null);
  const [initDetails, setInitDetails] = React.useState<IInitDetails>({});

  usePostMessageHandler('init', React.useCallback((e: CustomEvent) => {
    const { config, ...rest } = e.detail;
    if (config?.settings) {
      setConfig(config.settings as IConfig);
      setInitDetails(rest);
    }
  }, [config, setConfig]));

  usePostMessageHandler('setConfig', React.useCallback((e: CustomEvent) => {
    setConfig({
      ...config,
      ...e.detail,
    });
  }, [config, setConfig]));

  if (!config) {
    return null;
  }

  return (
    <ConfigContext.Provider value={{ config, initDetails }}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => {
  const context = React.useContext(ConfigContext);
  if (!context) {
    throw new Error('Context misconfigured: ConfigContext');
  }
  return context.config;
};

export const useInitDetails = () => {
  const context = React.useContext(ConfigContext);
  if (!context) {
    throw new Error('Context misconfigured: ConfigContext');
  }
  return context.initDetails;
};
